import React from 'react';
import './LiveUrlPagination.css';

const LiveUrlPagination = ({
  pagination = { currentPage: 1, totalPages: 0, totalRecords: 0, pageSize: 20 },
  onPageChange,
  onPageSizeChange
}) => {
  const { currentPage, totalPages, totalRecords, pageSize } = pagination;

  if (totalRecords === 0) {
    return null;
  }

  const showPageControls = totalPages > 1;

  const handlePageClick = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange && onPageChange(page);
    }
  };

  const handleKeyPress = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page if not visible
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageClick(1)}
          className="page-btn"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="ellipsis">...</span>);
      }
    }

    // Add visible page numbers
    for (let page = startPage; page <= endPage; page++) {
      pages.push(
        <button
          key={page}
          onClick={() => handlePageClick(page)}
          className={`page-btn ${page === currentPage ? 'active' : ''}`}
          aria-current={page === currentPage ? 'page' : undefined}
        >
          {page}
        </button>
      );
    }

    // Add last page if not visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="ellipsis">...</span>);
      }
      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageClick(totalPages)}
          className="page-btn"
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="live-url-pagination" data-testid="live-url-pagination">
      <div className="pagination-info">
        <span>
          Showing {startRecord} to {endRecord} of {totalRecords} records
        </span>
      </div>
      
      <div className="pagination-controls">
        {showPageControls && (
          <>
            <button
              onClick={() => handlePageClick(currentPage - 1)}
              disabled={currentPage <= 1}
              className="page-btn nav-btn"
              aria-label="Previous page"
            >
              Previous
            </button>

            {renderPageNumbers()}

            <button
              onClick={() => handlePageClick(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="page-btn nav-btn"
              aria-label="Next page"
            >
              Next
            </button>
          </>
        )}
      </div>

      <div className="page-size-selector">
        <label htmlFor="page-size">Records per page:</label>
        <select
          id="page-size"
          value={pageSize}
          onChange={(e) => {
            if (onPageSizeChange) {
              onPageSizeChange(parseInt(e.target.value, 10));
            }
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
  );
};

export default LiveUrlPagination;